/* eslint-disable react/require-default-props */
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import withStyles from '@mui/styles/withStyles'
import makeStyles from '@mui/styles/makeStyles'
import { FormControl, FormHelperText } from '@mui/material'
import { useStores } from 'stores/utils/hooks/useStores'
import { observer } from 'mobx-react'
import { ReactNode, ChangeEvent, useCallback, useMemo, memo } from 'react'
import FieldDataCollector from '../FieldDataCollector'
import { PathFormFieldProps } from '../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../utils/tracking/amplitude/WithFieldInteraction'
import { usePatchedField } from '../../stores/utils/hooks/usePatchedField'
import WithIncreasedEmphasis from './WithIncreasedEmphasis'

type Props = Omit<
  PathFormFieldProps<boolean>,
  'defaultValue' | 'tooltip' | 'onTooltip' | 'showTooltip'
> & {
  castToNumber?: boolean
  type?: boolean
  disabled?: boolean
  label?: ReactNode
  increaseEmphasis?: boolean
  alignOnTop?: boolean
}

const SmallControlLabel = withStyles(() =>
  createStyles({
    label: {
      color: 'rgb(45, 55, 72)',
      letterSpacing: 0,
      lineHeight: '1.25rem'
    }
  })
)(FormControlLabel)

const useStyles = makeStyles<
  Theme,
  {
    hasError: boolean
    increaseEmphasis: boolean
    isChecked: boolean
    alignOnTop: boolean
  }
>((theme) =>
  createStyles({
    checkbox: {
      '& svg': {
        color: ({ hasError, isChecked }) => {
          if (isChecked) {
            return theme.palette.primary.main
          }

          if (hasError) {
            return theme.palette.error.main
          }

          return theme.palette.grey[700]
        }
      }
    },
    formLabel: {
      '& > span': {
        fontSize: ({ increaseEmphasis }) =>
          increaseEmphasis ? theme.typography.body1.fontSize : '0.8125rem',
        paddingTop: ({ alignOnTop }) => (alignOnTop ? 0 : 'inherit'),
        alignSelf: ({ alignOnTop }) => (alignOnTop ? 'flex-start' : 'unset')
      }
    }
  })
)

const StyledFormControl = withStyles(() =>
  createStyles({
    root: {
      width: '100%'
    }
  })
)(FormControl)

const YesNoCheckbox = observer(
  ({
    path,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    castToNumber,
    label,
    increaseEmphasis = false,
    alignOnTop,
    disabled
  }: Props) => {
    const { input, error } = usePatchedField(path, {
      type: 'checkbox'
    })

    const {
      traversal: { removeErrorsForPath }
    } = useStores()

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLDivElement>) => {
        input.onChange(e)
        input.onBlur()
        removeErrorsForPath(path)
      },
      [input, removeErrorsForPath, path]
    )

    const isEmphasized = useMemo(
      () => increaseEmphasis && !input.checked,
      [increaseEmphasis, input.checked]
    )

    const hasError = !!error
    const { checkbox, formLabel } = useStyles({
      hasError,
      increaseEmphasis,
      isChecked: !!input.checked,
      alignOnTop: alignOnTop || false
    })

    return (
      <StyledFormControl error={hasError}>
        <SmallControlLabel
          className={formLabel}
          control={
            <WithIncreasedEmphasis display={isEmphasized}>
              <WithFieldInteraction
                trackFocusEvents={false}
                trackTooltipClick={false}
                trackOnChange
                path={path}
              >
                <Checkbox
                  className={checkbox}
                  checked={input.checked}
                  name={input.name}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  value={input.value}
                  disabled={disabled}
                  onChange={handleChange}
                  color="primary"
                />
              </WithFieldInteraction>
            </WithIncreasedEmphasis>
          }
          label={label}
        />
        {error && <FormHelperText>{error}</FormHelperText>}
      </StyledFormControl>
    )
  }
)

const PathCheckboxYesNo = (props: Props) => {
  const {
    path,
    labelPath,
    label,
    castToNumber,
    increaseEmphasis,
    alignOnTop,
    type,
    disabled
  } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <YesNoCheckbox
        path={path}
        labelPath={labelPath}
        label={label}
        castToNumber={castToNumber}
        increaseEmphasis={increaseEmphasis}
        alignOnTop={alignOnTop}
        type={type}
        disabled={disabled}
      />
    </FieldDataCollector>
  )
}

export default memo(PathCheckboxYesNo)
